import { Content } from "../../components/Content/Content";
import { Section } from "../../components/Section/Section";
import { FadeInUp } from "../../components/Animation/ScrollAnimation";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { ImageType } from "../../types/baseTypes";
import { ImageSliderOptionsType } from "@/types/ModuleOptionsType";
import { downloadFile } from "../../utils/downloadFile";
import styles from "./ImageSlider.module.scss";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNavigation,
} from "@aleksundshantu/aunds-utils-react/primitives/carousel";
import { Modul_Bild_Slider } from "../../wp/types/acf-module-types";
import { useSublinesVisibility } from "../../hooks/useSublinesOverlinesVisibility";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { columnSize } from "@/utils/layout/columnSize";
import { MediaItem } from "@/components/MediaItem/MediaItem";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";

export type ImageSliderModuleType = Modul_Bild_Slider;

export type ImageSliderElementType = {
  image_format: "portrait" | "square" | "landscape";
  image: ImageType | false;
};

interface ImageSliderModuleProps {
  data: ImageSliderModuleType;
  imageSliderOptions: ImageSliderOptionsType;
}

const ImageSliderModule = ({
  data,
  imageSliderOptions,
}: ImageSliderModuleProps) => {
  const {
    background_color_class,
    id,
    module_width,
    section_margin_class,
    section_padding_class,
    headline_text_align_class,
  } = data.layout;

  const {
    headline,
    headline_html_tag,
    elements,
    slider_type,
    overline,
    subline,
  } = data.image_slider;

  const {
    show_overline_subline,
    columns,
    box: { show_border_radius, show_box_shadow, show_card_box },
    slider_options: {
      has_autoplay,
      has_looping,
      slider_height,
      slider_navigation,
    },
  } = imageSliderOptions;

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline,
  );

  const { getIcon, getLabel } = useGlobalContext();

  {
    /* jscpd:ignore-start */
  }
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }

  const SliderJSX = (
    <Carousel autoplay={has_autoplay} loop={has_looping}>
      <CarouselContent spacing="32px" outerPadding="0 32px">
        {(elements || [])?.map((element, index) => {
          const { image, image_format } = element;
          const imageFilename = image && image.filename ? image.filename : "";
          return (
            <CarouselItem
              key={index}
              data-type={image_format}
              className={`${styles["image-slide"]} `}
              data-shadow={show_card_box && show_box_shadow}
              data-border-radius={show_card_box && show_border_radius}
              itemHeight={{
                ...(slider_height?.mobile && {
                  mobile: `${slider_height.mobile}px`,
                }),
                ...(slider_height?.tablet && {
                  tablet: `${slider_height.tablet}px`,
                }),
                ...(slider_height?.desktop && {
                  desktop: `${slider_height.desktop}px`,
                }),
                ...(slider_height?.desktop && {
                  fullhd: `${slider_height.fullhd}px`,
                }),
              }}
            >
              {image && (
                <MediaItem
                  objectFit="cover"
                  image_desktop={image}
                  sizes={
                    image_format === "landscape"
                      ? "(max-width: 767px) 343px, 418px"
                      : "(max-width: 767px) 156px, 193px"
                  }
                />
              )}

              {slider_type === "download" && image && (
                <button
                  type="button"
                  aria-label={getLabel("arialabel_download_slider").replace(
                    `"$filename"`,
                    imageFilename,
                  )}
                  onClick={() => {
                    downloadFile(image.url).catch((e) => {
                      console.error("Download failed:", e);
                    });
                  }}
                  className={styles["download-link"]}
                >
                  <CustomSVG
                    src={getIcon("download")}
                    wrapper={"span"}
                    aria-hidden="true"
                    className="is-flex"
                  />
                </button>
              )}
            </CarouselItem>
          );
        })}
      </CarouselContent>
      <CarouselNavigation type={slider_navigation} />
    </Carousel>
  );

  return (
    <Section
      id={id}
      className={`${styles["image-slider"]} ${background_color_class} ${section_margin_class} ${section_padding_class} `}
      frameless={module_width === "frameless"}
      columnsClassName="is-multiline is-centered"
    >
      <div
        className={`column  ${module_width === "narrow-frame" ? columnSize(columns) : "is-12"}`}
      >
        <FadeInUp>
          <Content
            headline={headline}
            headline_html_tag={headline_html_tag}
            headline_text_align_class={headline_text_align_class!}
            text_align_class="has-text-centered"
            overline={showOverline ? overline : ""}
            subline={showSubline ? subline : ""}
            text=""
            link={false}
            button_type="primary"
            frameless={module_width === "frameless"}
          />
        </FadeInUp>
        <FadeInUp>{SliderJSX}</FadeInUp>
      </div>
    </Section>
  );
};

export default ImageSliderModule;
